import { StatsigClient } from "@statsig/js-client";
import { startStatsigSessionReplay } from "./sessionReplay";
import { startStatsigWebAnalytics } from "./webAnalytics";
const DEFAULT_CONFIG = Object.freeze({
    environmentTier: "production",
    webAnalytics: Object.freeze({ enabled: false }),
});
let initPromise = null;
let client = null;
let currentClientKey = null;
let sessionReplayStarted = false;
let webAnalyticsStarted = false;
export function getStatsigClient() {
    return client;
}
export function hasStatsigClient() {
    return !!client;
}
export function getStatsigStableID() {
    return client?.getContext?.()?.stableID ?? null;
}
export async function initStatsigClient(options = {}) {
    const config = { ...DEFAULT_CONFIG, ...options };
    if (!config.clientKey) {
        throw new Error("Statsig clientKey is required.");
    }
    const shouldRestartForPlugin = (config.sessionReplay?.enabled && !sessionReplayStarted) ||
        (config.webAnalytics?.enabled && !webAnalyticsStarted);
    if (client &&
        currentClientKey &&
        (currentClientKey !== config.clientKey || shouldRestartForPlugin)) {
        await shutdownStatsigClient();
    }
    if (initPromise) {
        return initPromise;
    }
    initPromise = (async () => {
        const initOptions = {};
        if (config.environmentTier) {
            initOptions.environment = { tier: config.environmentTier };
        }
        const user = config.user || {};
        const nextClient = new StatsigClient(config.clientKey, user, initOptions);
        currentClientKey = config.clientKey;
        if (config.sessionReplay?.enabled && !sessionReplayStarted) {
            try {
                await startStatsigSessionReplay(nextClient, config.sessionReplay.options || {});
                sessionReplayStarted = true;
            }
            catch (error) {
                console.warn("Statsig Session Replay setup failed", error);
            }
        }
        if (config.webAnalytics?.enabled && !webAnalyticsStarted) {
            try {
                await startStatsigWebAnalytics(nextClient, config.webAnalytics.options || {});
                webAnalyticsStarted = true;
            }
            catch (error) {
                console.warn("Statsig Web Analytics setup failed", error);
            }
        }
        await nextClient.initializeAsync();
        const stableID = nextClient.getContext?.()?.stableID;
        if (stableID) {
            await nextClient.updateUserAsync({
                ...user,
                customIDs: { ...user.customIDs, stableID },
            });
        }
        client = nextClient;
        return nextClient;
    })().catch((error) => {
        client = null;
        currentClientKey = null;
        sessionReplayStarted = false;
        webAnalyticsStarted = false;
        initPromise = null;
        throw error;
    });
    return initPromise;
}
export function waitForInitializingStatsigClient(timeoutMs) {
    if (!initPromise)
        return Promise.resolve(null);
    return Promise.race([
        initPromise,
        new Promise((resolve) => window.setTimeout(() => resolve(null), timeoutMs)),
    ]);
}
export async function shutdownStatsigClient() {
    if (client && typeof client.shutdown === "function") {
        await client.shutdown();
    }
    client = null;
    currentClientKey = null;
    sessionReplayStarted = false;
    webAnalyticsStarted = false;
    initPromise = null;
}
